import type { SchemaField } from '@cli-types/core-engine/schema.js'
import { quotePropertyName, singularize, toPascalCase } from '@cli-utils/text.js'
import { isUserRelationshipTarget } from '@core-engine/schema/system-relationships/is-user-relationship-target.js'
import { assertSupportedSchemaFieldTree } from '@core-engine/schema/type-mappers/validation/assert-supported-schema-field-tree.js'

export function toTypeScriptType(field: SchemaField, mode: 'input' | 'output' = 'output'): string {
  assertSupportedSchemaFieldTree(field)

  switch (field.type) {
    case 'uuid':
    case 'serial':
      return 'string'
    case 'number':
    case 'decimal':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'string':
    case 'email':
    case 'varchar':
    case 'text':
    case 'markdown':
    case 'richtext':
    case 'date':
    case 'time':
    case 'timestamp':
    case 'image':
    case 'video':
    case 'media':
    case 'icon':
      return 'string'
    case 'select':
      return field.multiple ? 'string[]' : 'string'
    case 'relationship':
      if (mode === 'input') {
        return field.multiple ? 'string[]' : 'string'
      }
      if (isUserRelationshipTarget(field.relationship)) {
        return field.multiple ? 'UserData[]' : 'UserData | null'
      }
      if (field.relationship) {
        const relationshipSingular = singularize(field.relationship)
        const relationshipPascal = toPascalCase(relationshipSingular)
        return field.multiple ? `${relationshipPascal}Data[]` : `${relationshipPascal}Data | null`
      }
      return 'string'
    case 'group':
      if (field.fields && field.fields.length > 0) {
        const groupType = field.fields
          .map((f) => {
            const type = toTypeScriptType(f, mode)
            return `${quotePropertyName(f.name)}?: ${type}`
          })
          .join('; ')
        return `{ ${groupType} }`
      }
      return 'Record<string, unknown>'
    case 'list':
      if (field.fields && field.fields.length > 0) {
        const nestedType = field.fields
          .flatMap((f) => {
            const type = toTypeScriptType(f, 'input')
            const fields = [`${quotePropertyName(f.name)}?: ${type}`]
            if (f.hasIcon) {
              fields.push(`${quotePropertyName(`${f.name}Icon`)}?: string`)
            }
            return fields
          })
          .join('; ')
        return `Array<{ ${nestedType} }>`
      }
      return 'string[]'
    case 'gallery':
      return 'string[]'
    default:
      return 'string'
  }
}
